import React, { useRef } from "react";
import { Button } from "./ui/button";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Fix C (2026-08-24): foto real del hierro, además del dibujo a mano
 * (IronBrandCanvas). Se comprime SIEMPRE en el navegador antes de guardar
 * (lado mayor máx 1200px, JPEG calidad 0.7) porque hay 3.090 animales activos
 * y una foto de cámara sin comprimir (3-6 MB) inflaría la base, los respaldos
 * diarios y la sincronización offline hasta romperlos.
 */

const MAX_SIDE = 1200;
const MAX_BYTES = 1024 * 1024; // 1 MB

function compress(file, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > MAX_SIDE) {
          height = Math.round((height * MAX_SIDE) / width);
          width = MAX_SIDE;
        } else if (height > MAX_SIDE) {
          width = Math.round((width * MAX_SIDE) / height);
          height = MAX_SIDE;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function dataUrlBytes(dataUrl) {
  const base64 = dataUrl.split(",")[1] || "";
  return Math.round((base64.length * 3) / 4);
}

export function IronBrandPhoto({ value, onChange }) {
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      let dataUrl = await compress(file, 0.7);
      if (dataUrlBytes(dataUrl) > MAX_BYTES) {
        dataUrl = await compress(file, 0.5);
        if (dataUrlBytes(dataUrl) > MAX_BYTES) {
          toast.error("La foto es muy pesada, probá con otra");
          return;
        }
      }
      onChange(dataUrl);
    } catch (err) {
      toast.error("No pude procesar la foto");
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid="iron-brand-photo">
      {value ? (
        <img
          src={value}
          alt="Foto del hierro"
          className="max-h-[160px] rounded-md border border-border object-contain bg-white"
          data-testid="iron-brand-photo-preview"
        />
      ) : (
        <div className="h-16 w-24 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
          Sin foto
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} data-testid="iron-brand-photo-button">
          <Camera className="h-3.5 w-3.5 mr-1.5" /> {value ? "Cambiar foto" : "Subir foto del hierro"}
        </Button>
        {value && (
          <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => onChange(null)} data-testid="iron-brand-photo-clear">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Quitar foto
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="iron-brand-photo-input"
      />
    </div>
  );
}
